import { getDownloadURL, getMetadata, ref as storageRef } from 'firebase/storage';
import { storage } from '@/firebase';

type AttachmentLink = {
  path?: string;
  name?: string;
  url?:  string;
};

const ALLOWED_STORAGE_ROOTS = /^(attachments|docs|userDocs)\//;

export function attachmentViewerUrl(attachment: AttachmentLink): string {
  const path = attachment.path?.trim();
  if (!path) return attachment.url ?? '#';

  const params = new URLSearchParams({ arquivo: path });
  if (attachment.name) params.set('nome', attachment.name);
  return `/anexo?${params.toString()}`;
}

export function isAttachmentViewerRoute(pathname = window.location.pathname): boolean {
  return pathname === '/anexo' || pathname === '/anexo/';
}

function viewerParams(): { path: string; name: string } {
  const params = new URLSearchParams(window.location.search);
  return {
    path: params.get('arquivo')?.trim() ?? '',
    name: params.get('nome')?.trim() ?? 'Anexo',
  };
}

function showViewerState(message: string, isError = false): void {
  const content = document.getElementById('attachment-viewer-content');
  if (!content) return;
  content.replaceChildren();

  const state = document.createElement('div');
  state.className = `attachment-viewer-state${isError ? ' error' : ''}`;
  state.textContent = message;
  content.appendChild(state);
}

function renderPreview(url: string, name: string, contentType: string): void {
  const content = document.getElementById('attachment-viewer-content');
  const download = document.getElementById('attachment-viewer-download') as HTMLAnchorElement | null;
  if (!content) return;

  content.replaceChildren();
  if (download) {
    download.href = url;
    download.download = name;
    download.style.display = '';
  }

  let preview: HTMLElement;
  if (contentType.startsWith('image/')) {
    const image = document.createElement('img');
    image.className = 'attachment-viewer-image';
    image.src = url;
    image.alt = name;
    preview = image;
  } else if (contentType.startsWith('video/')) {
    const video = document.createElement('video');
    video.className = 'attachment-viewer-media';
    video.src = url;
    video.controls = true;
    preview = video;
  } else if (contentType.startsWith('audio/')) {
    const audio = document.createElement('audio');
    audio.className = 'attachment-viewer-audio';
    audio.src = url;
    audio.controls = true;
    preview = audio;
  } else if (contentType === 'application/pdf' || contentType.startsWith('text/')) {
    const frame = document.createElement('iframe');
    frame.className = 'attachment-viewer-frame';
    frame.src = url;
    frame.title = name;
    preview = frame;
  } else {
    const unsupported = document.createElement('div');
    unsupported.className = 'attachment-viewer-state';
    unsupported.innerHTML = '<span class="attachment-viewer-file-icon">📎</span><span>Este tipo de arquivo não possui visualização no navegador.</span><span>Use o botão “Baixar arquivo” para abri-lo.</span>';
    preview = unsupported;
  }

  content.appendChild(preview);
}

export async function openAttachmentViewer(): Promise<void> {
  const { path, name } = viewerParams();
  const nameEl = document.getElementById('attachment-viewer-name');
  const download = document.getElementById('attachment-viewer-download') as HTMLAnchorElement | null;

  if (nameEl) nameEl.textContent = name;
  if (download) download.style.display = 'none';
  document.title = `${name} · Travelle`;
  showViewerState('Carregando arquivo…');

  if (!path || !ALLOWED_STORAGE_ROOTS.test(path) || path.includes('..')) {
    showViewerState('O endereço deste anexo é inválido.', true);
    return;
  }

  try {
    const fileRef = storageRef(storage, path);
    const [url, metadata] = await Promise.all([
      getDownloadURL(fileRef),
      getMetadata(fileRef),
    ]);
    renderPreview(url, name, metadata.contentType ?? '');
  } catch (error) {
    console.error('[attachment-viewer] Falha ao carregar anexo:', error);
    showViewerState('Não foi possível abrir este anexo. Verifique se você ainda tem acesso ao arquivo.', true);
  }
}

export function closeAttachmentViewer(): void {
  window.close();
}
